import {Injectable} from '@angular/core';
import {Observable, ConnectableObservable, Subscription} from 'rxjs';
import {publishReplay, first} from 'rxjs/operators';
import {CurrentUserService} from '../../cache/currentuser/currentuser.service';
import {UIConfigurationService} from '../../cache/uiconfiguration/uiconfiguration.service';
import {NavigationBarConfigurationService} from '../../cache/uiconfiguration/navigationbarconfiguration.service';
import {SearchScopeConfigurationService} from '../../cache/uiconfiguration/searchscopeconfiguration.service';
import {ActionBarConfigurationService} from '../../cache/uiconfiguration/actionbarconfiguration.service';
import {DialogConfigurationService} from '../../cache/uiconfiguration/dialogconfiguration.service';
import {SchemaService} from '../../cache/schema/schema.service';
import {SupportedLocaleConfigurationService} from '../../cache/uiconfiguration/supportedlocaleconfiguration.service';
import {HomePageConfigurationService} from '../../cache/uiconfiguration/homepageconfiguration.service';
import {TimeZoneConfigurationService} from '../../cache/uiconfiguration/timezoneconfiguration.service';

export interface StartupConfigurations {
  currentUser: ConnectableObservable<any>;
  uiConfiguration: ConnectableObservable<any>;
  navigationBar: ConnectableObservable<any>;
  searchScope: ConnectableObservable<any>;
  actionBar: ConnectableObservable<any>;
  dialog: ConnectableObservable<any>;
  attributeTypes: ConnectableObservable<any>;
  objectTypes: ConnectableObservable<any>;
  bindings: ConnectableObservable<any>;
  locale: ConnectableObservable<any>;
  homepage: ConnectableObservable<any>;
  timezone: ConnectableObservable<any>;
}

@Injectable({
  providedIn: 'root',
})
export class StartupApiService {
  constructor(
    private currentUserService: CurrentUserService,
    private uiConfigService: UIConfigurationService,
    private navBarService: NavigationBarConfigurationService,
    private searchScopeService: SearchScopeConfigurationService,
    private actionBarService: ActionBarConfigurationService,
    private dialogService: DialogConfigurationService,
    private schemaService: SchemaService,
    private localeService: SupportedLocaleConfigurationService,
    private homepageService: HomePageConfigurationService,
    private timezoneService: TimeZoneConfigurationService,
  ) {}

  loadAllConfigurations(): StartupConfigurations {
    return {
      currentUser: this.createConnectable(this.currentUserService.getCurrentUser()),
      uiConfiguration: this.createConnectable(this.uiConfigService.getUIConfiguration()),
      navigationBar: this.createConnectable(this.navBarService.getNavigationBarConfigurations()),
      searchScope: this.createConnectable(this.searchScopeService.getAll()),
      actionBar: this.createConnectable(this.actionBarService.getActionBarConfigurations()),
      dialog: this.createConnectable(this.dialogService.getDialogConfigurations()),
      attributeTypes: this.createConnectable(this.schemaService.getAttributeTypeDescriptions()),
      objectTypes: this.createConnectable(this.schemaService.getObjectTypeDescriptions()),
      bindings: this.createConnectable(this.schemaService.getBindingDescriptions()),
      // Use the existing locale observable that was already created and connected
      locale: this.localeService.getSupportedLocaleConfiguration() as ConnectableObservable<any>,
      homepage: this.createConnectable(this.homepageService.getHomePageConfigurations()),
      timezone: this.createConnectable(this.timezoneService.getAll()),
    };
  }

  private createConnectable<T>(observable$: Observable<T>): ConnectableObservable<T> {
    return observable$.pipe(first(), publishReplay(1)) as ConnectableObservable<T>;
  }

  connectAll(configurations: StartupConfigurations, subscriptions: Subscription[]): void {
    // Note: locale is already connected in CacheService.refreshAllData()
    // Do NOT connect it again to avoid duplicate connections
    subscriptions.push(configurations.currentUser.connect());
    subscriptions.push(configurations.uiConfiguration.connect());
    subscriptions.push(configurations.navigationBar.connect());
    subscriptions.push(configurations.searchScope.connect());
    subscriptions.push(configurations.actionBar.connect());
    subscriptions.push(configurations.dialog.connect());
    subscriptions.push(configurations.attributeTypes.connect());
    subscriptions.push(configurations.objectTypes.connect());
    subscriptions.push(configurations.bindings.connect());
    subscriptions.push(configurations.homepage.connect());
    subscriptions.push(configurations.timezone.connect());
  }
}
